/**
 * Motor de permissão (D-69 · DS-16 · spec 05 §9).
 *
 * Vive no domínio puro, e não dentro do guard do NestJS, por um motivo prático: a
 * spec 08 §3 exige uma **matriz tabular completa** — cada nível × cada módulo ×
 * cada ação → esperado. Com a decisão aqui, essa matriz é um teste de função pura
 * que roda em milissegundos e cobre todas as combinações; o guard fica com o que é
 * dele, que é ler a sessão e traduzir a resposta em 404 ou 403.
 *
 * Duas leis que a DS-16 fixou e que o código respeita literalmente:
 *
 * - **A escada é CUMULATIVA**: quem pode `editar_todos` também pode `criar` e `ver`.
 * - **Os três marcadores são ORTOGONAIS**: excluir, aprovar e exportar não são
 *   degraus. Antes eram, e por isso o protótipo tinha "Excluir" no meio da escada —
 *   o que obrigava a dar poder de edição a quem só precisava aprovar.
 */

/** Os 25 módulos do sistema (spec 02 §1.8 — as chaves são EXATAS; `tributos` desde a v1.5.0, DS-615). */
export const MODULOS = [
  'dashboard',
  'tarefas',
  'crm',
  'negocios',
  'metas_previsao',
  'pedidos',
  'faturamento',
  'recebiveis',
  'comissoes',
  'midia',
  'midia_custos_margem',
  'tesouraria',
  'conciliacao',
  'contas_a_pagar',
  'aprovacao_desembolso',
  'fornecedores',
  'rh',
  'orcamento',
  'contabil_dre',
  'relatorios',
  'catalogos',
  'entidades_dv',
  'usuarios_permissoes',
  'auditoria',
  'tributos',
] as const

export type Modulo = (typeof MODULOS)[number]

/** A escada, do menor para o maior. A ordem do array É a hierarquia. */
export const ESCADA = ['sem_acesso', 'ver', 'criar', 'editar_proprio', 'editar_todos', 'total'] as const

export type Acao = (typeof ESCADA)[number]

export type Marcador = 'excluir' | 'aprovar' | 'exportar'

export type EscopoCarteira = 'proprios' | 'todos'

export interface PermissaoDeModulo {
  readonly acao: Acao
  readonly podeExcluir: boolean
  readonly podeAprovar: boolean
  readonly podeExportar: boolean
  readonly escopoCarteira: EscopoCarteira
  readonly veValoresFinanceiros: boolean
}

export interface NivelDeAcesso {
  readonly nome: string
  /** Módulo ausente do mapa = `sem_acesso` (a ausência é negação, nunca permissão). */
  readonly permissoes: Readonly<Partial<Record<Modulo, PermissaoDeModulo>>>
}

/** Permissão de quem não tem nada — o default de todo módulo não declarado. */
export const SEM_ACESSO: PermissaoDeModulo = {
  acao: 'sem_acesso',
  podeExcluir: false,
  podeAprovar: false,
  podeExportar: false,
  escopoCarteira: 'proprios',
  veValoresFinanceiros: false,
}

export function permissaoDe(nivel: NivelDeAcesso, modulo: Modulo): PermissaoDeModulo {
  return nivel.permissoes[modulo] ?? SEM_ACESSO
}

function degrau(acao: Acao): number {
  return ESCADA.indexOf(acao)
}

/**
 * A escada é cumulativa: ter `editar_todos` responde "sim" para `criar` e `ver`.
 */
export function alcanca(nivel: NivelDeAcesso, modulo: Modulo, exigida: Acao): boolean {
  return degrau(permissaoDe(nivel, modulo).acao) >= degrau(exigida)
}

/**
 * Marcador é ortogonal à escada, mas **exige `ver`**: conceder "aprovar" a quem não
 * enxerga o módulo seria poder invisível — a pessoa aprovaria o que não consegue
 * abrir. É a mesma regra que o CHECK da tabela cobra no banco.
 */
export function temMarcador(nivel: NivelDeAcesso, modulo: Modulo, marcador: Marcador): boolean {
  const p = permissaoDe(nivel, modulo)
  if (!alcanca(nivel, modulo, 'ver')) return false
  switch (marcador) {
    case 'excluir':
      return p.podeExcluir
    case 'aprovar':
      return p.podeAprovar
    case 'exportar':
      return p.podeExportar
  }
}

/** Vê a carteira dos outros, ou só a própria? Recorte por (nível, módulo). */
export function veCarteiraDeTodos(nivel: NivelDeAcesso, modulo: Modulo): boolean {
  return alcanca(nivel, modulo, 'ver') && permissaoDe(nivel, modulo).escopoCarteira === 'todos'
}

/** Ver a página é independente de ver os valores (CONCEITO §21.1). */
export function veValoresFinanceiros(nivel: NivelDeAcesso, modulo: Modulo): boolean {
  return alcanca(nivel, modulo, 'ver') && permissaoDe(nivel, modulo).veValoresFinanceiros
}

/**
 * 2FA obrigatório é DERIVADO do nível (spec 05 §9): quem aprova desembolso ou tem
 * `total` em Tesouraria mexe com dinheiro saindo, e entra com segundo fator.
 *
 * A coluna `usuario_interno.dois_fatores_obrigatorio` materializa esta resposta
 * porque o login precisa dela antes de resolver a matriz inteira — mas a FONTE é
 * esta função, e é ela que deve reescrever a coluna quando o nível mudar.
 */
export function exigeDoisFatores(nivel: NivelDeAcesso): boolean {
  return temMarcador(nivel, 'aprovacao_desembolso', 'aprovar') || alcanca(nivel, 'tesouraria', 'total')
}

/**
 * O que o guard responde a uma requisição.
 *
 * `nao_encontrado` e `proibido` são coisas diferentes de propósito (spec 03 §1.3):
 * quem não pode VER um recurso individual recebe **404**, indistinguível de "não
 * existe" — um 403 aqui contaria que o registro existe, e isso já é vazamento. A
 * negativa de MUTAÇÃO é 403 genérico, porque nesse ponto a existência já é sabida.
 */
export type VeredictoDeAcesso = 'permitido' | 'nao_encontrado' | 'proibido'

export function decidirAcesso(
  nivel: NivelDeAcesso,
  modulo: Modulo,
  exigida: Acao,
): VeredictoDeAcesso {
  if (!alcanca(nivel, modulo, 'ver')) return 'nao_encontrado'
  return alcanca(nivel, modulo, exigida) ? 'permitido' : 'proibido'
}

export function decidirMarcador(
  nivel: NivelDeAcesso,
  modulo: Modulo,
  marcador: Marcador,
): VeredictoDeAcesso {
  if (!alcanca(nivel, modulo, 'ver')) return 'nao_encontrado'
  return temMarcador(nivel, modulo, marcador) ? 'permitido' : 'proibido'
}
